import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui";
import { Button } from "@/components/ui/button"; // Added Button import
import useDebounce from "@/hooks/useDebounce";
import { useInfiniteOptions } from "@/hooks/useInfiniteOptions";
import { cn } from "@/lib/utils";
import toId from "@/utils/toId.utils";
import { Loader2, Plus } from "lucide-react"; // Added Plus icon
import { JSX, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FieldPath, FieldValues, useFormContext } from "react-hook-form";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";
import { AsyncSelectFetchFunction } from "../types";

interface FieldConfigProps<T extends FieldValues = FieldValues> {
  name: FieldPath<T>;
  fetchOptions: AsyncSelectFetchFunction;
  queryKey: string[];
  pageSize?: number;
  label?: string;
  placeholder?: string;
  description?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
  searchEnabled?: boolean;
  searchDebounce?: number;
  searchPlaceholder?: string;
  emptyMessage?: string;
  onCreateClick?: () => void; // Added props
  createLabel?: string;
  defaultOptions?: {
    label: string;
    value: string;
    disabled?: boolean;
    selectedLabel?: string;
  }[];
  className?: {
    itemClass?: string;
    triggerClass?: string;
    labelClass?: string;
    helperClass?: string;
    descriptionClass?: string;
    controlClass?: string;
    messageClass?: string;
  };
}

export type AsyncSingleSelectFieldProps<T extends FieldValues = FieldValues> =
  FieldConfigProps<T> & {
    value?: string;
    onValueChange?: (value: string) => void;
    tabIndex?: number;
    "aria-label"?: string;
    "aria-labelledby"?: string;
    style?: React.CSSProperties;
    [key: `data-${string}`]: string | number | boolean | undefined;
    /** Called in addition to RHF's onBlur */
    onOpenChange?: (open: boolean) => void;
  };

function AsyncSingleSelectField<T extends FieldValues = FieldValues>(
  props: AsyncSingleSelectFieldProps<T>
): JSX.Element | null {
  const { control } = useFormContext<T>();

  const {
    name,
    fetchOptions,
    queryKey,
    pageSize = 10,
    label,
    className,
    placeholder,
    description,
    helperText,
    required = false,
    disabled = false,
    searchEnabled = true,
    searchDebounce = 300,
    searchPlaceholder = "Search...",
    emptyMessage = "No options found",
    onCreateClick,
    createLabel,
    defaultOptions,
    value,
    onValueChange,
    onOpenChange,
    ...restProps
  } = props;

  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, searchDebounce);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    options,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error,
  } = useInfiniteOptions({
    queryKey,
    fetchFn: fetchOptions,
    pageSize,
    search: debouncedSearch,
    enabled: !disabled,
  });

  // Merge defaultOptions with fetched options
  const combinedOptions = useMemo(() => {
    const currentOptions = options || [];
    if (!defaultOptions?.length) return currentOptions;

    // Create a Set of existing IDs to avoid duplicates
    const existingIds = new Set(currentOptions.map((o) => o.value));

    // Filter defaultOptions to only include items NOT in currentOptions
    const missingDefaults = defaultOptions.filter(
      (d) => !existingIds.has(d.value)
    );

    // Return defaults first, then fetched options
    return [...missingDefaults, ...currentOptions];
  }, [options, defaultOptions]);

  // ✅ Maintain focus on input after re-renders
  useEffect(() => {
    if (inputRef.current && searchEnabled) {
      const input = inputRef.current;
      const currentValue = input.value;
      const cursorPosition = input.selectionStart;

      // Restore focus and cursor position if it was focused
      if (document.activeElement !== input && currentValue === searchQuery) {
        input.focus();
        if (cursorPosition !== null) {
          input.setSelectionRange(cursorPosition, cursorPosition);
        }
      }
    }
  }, [options, searchQuery, searchEnabled]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      const target = e.currentTarget;
      const scrollPercentage =
        (target.scrollTop / (target.scrollHeight - target.clientHeight)) * 100;

      if (
        scrollPercentage > 80 &&
        hasNextPage &&
        !isFetchingNextPage &&
        !isLoading
      ) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, isLoading, fetchNextPage]
  );

  // Validation
  if (!name || !control) {
    if (process.env.NODE_ENV === "development") {
      console.error(
        "AsyncSingleSelectField: 'name' and 'control' are required"
      );
    }
    return null;
  }

  if (!fetchOptions) {
    if (process.env.NODE_ENV === "development") {
      console.error("AsyncSingleSelectField: 'fetchOptions' is required");
    }
    return null;
  }

  const {
    itemClass,
    labelClass,
    triggerClass,
    helperClass,
    descriptionClass,
    controlClass,
    messageClass,
  } = className ?? {};

  const safeId = toId(name);
  const errorId = `${safeId}-error`;
  const descriptionId = description ? `${safeId}-description` : undefined;
  const helperTextId = helperText ? `${safeId}-helper` : undefined;

  return (
    <FormField
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const ariaDescribedBy = [
          helperTextId,
          descriptionId,
          fieldState.error ? errorId : undefined,
        ]
          .filter(Boolean)
          .join(" ");

        const currentValue =
          value !== undefined
            ? String(value)
            : field.value
              ? String(field.value)
              : undefined;

        const selectedOption = combinedOptions?.find(
          (o) => String(o.value) === String(currentValue)
        );

        return (
          <FormItem className={cn("w-full space-y-1", itemClass)}>
            <FormLabel
              className={cn(
                "flex items-center gap-1 text-sm font-medium",
                !label && "sr-only",
                labelClass
              )}
              htmlFor={safeId}
            >
              <span>{label || name}</span>
              {required && <span className="text-destructive">*</span>}
            </FormLabel>

            {helperText && (
              <p
                id={helperTextId}
                className={cn("text-muted-foreground text-xs", helperClass)}
              >
                {helperText}
              </p>
            )}

            <Select
              disabled={disabled}
              {...field}
              onValueChange={(value: string) => {
                field.onChange(value);
                onValueChange?.(value);
              }}
              onOpenChange={(open: boolean) => {
                if (!open) {
                  field.onBlur();
                }
                onOpenChange?.(open);
              }}
              value={currentValue}
            >
              <FormControl className={cn("", controlClass)}>
                <SelectTrigger
                  id={safeId}
                  className={cn("w-full", triggerClass)}
                  aria-label={props["aria-label"] || label}
                  aria-describedby={ariaDescribedBy || undefined}
                  aria-invalid={fieldState.error ? "true" : "false"}
                  aria-required={required}
                  onBlur={field.onBlur}
                  {...restProps}
                >
                  {selectedOption ? (
                    <SelectValue key="value">
                      <span>
                        {selectedOption.selectedLabel || selectedOption.label}
                      </span>
                    </SelectValue>
                  ) : (
                    <SelectValue
                      key="placeholder"
                      placeholder={
                        isLoading
                          ? "Loading options..."
                          : placeholder || `Select ${label || name}`
                      }
                    />
                  )}
                </SelectTrigger>
              </FormControl>

              <SelectContent>
                <div
                  ref={scrollContainerRef}
                  onScroll={handleScroll}
                  className="max-h-[300px] overflow-y-auto"
                >
                  {searchEnabled && (
                    <div className="bg-popover sticky top-0 z-10 p-2">
                      <input
                        ref={inputRef}
                        key="search-input-stable"
                        type="text"
                        className="focus:ring-ring w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2"
                        placeholder={searchPlaceholder}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onMouseDown={(e) => {
                          // Prevent select from closing when clicking input
                          e.stopPropagation();
                        }}
                        onKeyDown={(e) => {
                          // Prevent select from closing on arrow keys
                          e.stopPropagation();
                        }}
                        disabled={disabled}
                        autoFocus
                      />
                    </div>
                  )}

                  <SelectGroup>
                    {isLoading && options.length === 0 && (
                      <div className="flex items-center justify-center p-4">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        <span className="text-muted-foreground ml-2 text-sm">
                          Loading options...
                        </span>
                      </div>
                    )}

                    {error && (
                      <div className="text-destructive p-4 text-center text-sm">
                        Error loading options. Please try again.
                      </div>
                    )}

                    {!isLoading && !error && options.length === 0 && (
                      <div className="text-muted-foreground p-4 text-center text-sm">
                        {emptyMessage}
                      </div>
                    )}

                    {combinedOptions.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        textValue={option.selectedLabel || option.label}
                      >
                        <span className="block max-w-[300px] whitespace-pre-wrap break-words text-left leading-snug sm:max-w-[300px]">
                          {option.label}
                        </span>
                      </SelectItem>
                    ))}

                    {isFetchingNextPage && (
                      <div className="flex items-center justify-center p-2">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        <span className="text-muted-foreground ml-2 text-xs">
                          Loading more...
                        </span>
                      </div>
                    )}
                  </SelectGroup>
                </div>

                {onCreateClick && (
                  <div className="border-t p-2">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="text-primary hover:text-primary hover:bg-primary/10 w-full justify-start gap-2 px-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        onCreateClick();
                      }}
                    >
                      <Plus className="h-4 w-4" />
                      {createLabel || "Add New"}
                    </Button>
                  </div>
                )}
              </SelectContent>
            </Select>

            {description && (
              <FormDescription
                id={descriptionId}
                className={cn(
                  "text-muted-foreground text-sm",
                  descriptionClass
                )}
              >
                {description}
              </FormDescription>
            )}

            <FormMessage className={cn("text-sm", messageClass)} id={errorId} />
          </FormItem>
        );
      }}
    />
  );
}

AsyncSingleSelectField.displayName = "AsyncSingleSelectField";

export default AsyncSingleSelectField;
